import { Alert, Button, Card, Input, Space, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { DatePicker as ZhDatePicker, Dialog as ZhDialog } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { SHIP_WAY_BILL_TYPE } from "@/meta/logistics/waybill";
import RequiredMark from "@/views/components/RequiredMark";

export interface WaybillManagementFormData {
    id?: string | number;
    serviceType?: number;
    payType?: number;
    srpId?: string | number;
    srcId?: string | number;
    goodsType?: string;
    ownerId?: number;
    ownerName?: string;
    ownerMobile?: string;
    ownerEnterprise?: { realName?: string; institutionName?: string; code?: string; mobile?: string };
    sourcePortId?: number;
    sourcePortName?: string;
    destinationPortId?: number;
    destinationPortName?: string;
    unitPrice?: number;
    ton?: number;
    loadDate?: string;
    loadDays?: number;
    loadUnloadDays?: number;
    demurrageFee?: number;
    maritimeAffairsFee?: number;
    shipId?: number;
    shipType?: number;
    shipName?: string;
    captainId?: number;
    captainName?: string;
    captainMobile?: string;
    captainEnterprise?: { realName?: string; code?: string; mobile?: string };
    captainShipInfoServiceFee?: number;
    ownerShipInfoServiceFee?: number;
    deposit?: number;
    shippingHandlerId?: number;
    shippingHandlerName?: string;
}

export interface WaybillManagementFormPageProps {
    loadWaybill?: (id: string) => Promise<WaybillManagementFormData>;
    createWaybill?: (data: WaybillManagementFormData) => Promise<unknown>;
    updateWaybill?: (id: string, data: WaybillManagementFormData) => Promise<unknown>;
}

/** 复用 Vue 船运单详情接口，为编辑态提供历史字段回显。 */
export async function loadWaybillManagementForm(id: string): Promise<WaybillManagementFormData> {
    const response = await shippingRequestHttp.service.get<{ transportOrderShip?: WaybillManagementFormData }>(
        `/transport/order/ship/detail/${encodeURIComponent(id)}`
    );
    return response.data.transportOrderShip || {};
}

/** 复用 Vue 服务运单新增接口。 */
export async function createWaybillManagement(data: WaybillManagementFormData) {
    const response = await shippingRequestHttp.service.post("/transport/order/ship", data);
    return response.data;
}

/** 复用 Vue 船运单编辑接口，历史实现使用 POST 更新。 */
export async function updateWaybillManagement(id: string, data: WaybillManagementFormData) {
    const response = await shippingRequestHttp.service.post(`/transport/order/ship/${encodeURIComponent(id)}`, data);
    return response.data;
}

const initialForm: WaybillManagementFormData = {
    serviceType: SHIP_WAY_BILL_TYPE.SUPERVISION.code,
    payType: 1,
    maritimeAffairsFee: 4,
    captainShipInfoServiceFee: 0,
    ownerShipInfoServiceFee: 0
};

// 自定义运单字段不经过 Ant Form.Item，按提交校验同步维护必填字段标识。
const requiredTextLabels = new Set([
    "货物类型",
    "联系人",
    "手机号",
    "始发地",
    "目的地",
    "船舶名称",
    "船主联系人",
    "船主手机号"
]);
const requiredNumberLabels = new Set([
    "货主 ID",
    "始发地 ID",
    "目的地 ID",
    "运价",
    "运货吨数",
    "装卸天数",
    "船舶 ID",
    "船主 ID"
]);

function numberValue(value: string): number | undefined {
    if (!value.trim()) return undefined;
    const result = Number(value);
    return Number.isFinite(result) ? result : undefined;
}

/** 船运单新增/编辑表单，优先覆盖 Vue 表单的核心字段和 payload 结构。 */
export function WaybillManagementFormPage({
    loadWaybill = loadWaybillManagementForm,
    createWaybill = createWaybillManagement,
    updateWaybill = updateWaybillManagement
}: WaybillManagementFormPageProps) {
    const location = useLocation();
    const { id: routeId } = useParams();
    const isEdit = location.pathname.includes("waybillManagementEdit");
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const [form, setForm] = useState<WaybillManagementFormData>(initialForm);
    const [errorMessage, setErrorMessage] = useState("");
    // 保存待确认的 Vue 运单 payload，避免点击提交时绕过 ZhConfirm 直接写入接口。
    const [pendingPayload, setPendingPayload] = useState<WaybillManagementFormData | null>(null);

    useEffect(() => {
        if (!isEdit || !id) return;
        let disposed = false;
        void loadWaybill(id)
            .then(data => {
                if (!disposed) setForm(current => ({ ...current, ...data }));
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船运单回显失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, isEdit, loadWaybill]);

    const setField = <K extends keyof WaybillManagementFormData>(key: K, value: WaybillManagementFormData[K]) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    /** 保留 Vue 核心字段校验，避免把不完整运单提交给接口。 */
    const validate = () => {
        if (!form.goodsType?.trim()) return "请输入货物类型";
        if (!form.ownerId) return "请选择货主";
        if (!form.ownerName?.trim()) return "请输入联系人";
        if (!/^1[3456789]\d{9}$/.test(form.ownerMobile || "")) return "请输入正确的手机号";
        if (!form.sourcePortId || !form.sourcePortName?.trim()) return "请选择始发地";
        if (!form.destinationPortId || !form.destinationPortName?.trim()) return "请选择目的地";
        if (form.sourcePortId === form.destinationPortId) return "始发地和目的地不可为同一地";
        if (!form.unitPrice || form.unitPrice <= 0 || form.unitPrice > 100000) return "请输入有效运价";
        if (!form.ton || form.ton <= 0 || form.ton > 1000000 || !Number.isInteger(form.ton))
            return "请输入有效运货吨数";
        if (!form.loadDate?.trim()) return "请选择装载日期";
        if (!form.loadUnloadDays || form.loadUnloadDays <= 0 || form.loadUnloadDays > 30) return "请输入有效装卸天数";
        if (!form.shipId || !form.shipName?.trim()) return "请选择承运船舶";
        if (!form.captainId) return "请选择船主";
        if (!form.captainName?.trim()) return "请输入船主联系人";
        if (!/^1[3456789]\d{9}$/.test(form.captainMobile || "")) return "请输入正确的船主手机号";
        return "";
    };

    const submit = () => {
        const validationError = validate();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        const payload: WaybillManagementFormData = {
            ...form,
            serviceType: Number(form.serviceType),
            payType: Number(form.payType),
            ownerId: Number(form.ownerId),
            sourcePortId: Number(form.sourcePortId),
            destinationPortId: Number(form.destinationPortId),
            unitPrice: Number(form.unitPrice),
            ton: Number(form.ton),
            loadDays: Number(form.loadDays || 0),
            loadUnloadDays: Number(form.loadUnloadDays),
            shipId: Number(form.shipId),
            captainId: Number(form.captainId),
            ownerEnterprise: form.ownerEnterprise || { realName: form.ownerName, mobile: form.ownerMobile },
            captainEnterprise: form.captainEnterprise || { realName: form.captainName, mobile: form.captainMobile }
        };
        setPendingPayload(payload);
    };

    /** 复刻 Vue ZhConfirm：确认后才调用新增/编辑接口。 */
    const confirmSubmit = async () => {
        if (!pendingPayload) return;
        const payload = pendingPayload;
        setPendingPayload(null);
        try {
            if (isEdit) await updateWaybill(id, payload);
            else await createWaybill(payload);
            message.success(isEdit ? "修改成功" : "添加成功");
            navigate(-1);
        } catch {
            setErrorMessage(isEdit ? "船运单修改失败" : "船运单创建失败");
        }
    };

    const textField = (label: string, key: keyof WaybillManagementFormData) => (
        <label>
            <span>
                {requiredTextLabels.has(label) ? <RequiredMark /> : null}
                {label}
            </span>
            <Input
                aria-label={label}
                placeholder={
                    {
                        货物类型: "请输入货物类型",
                        联系人: "请输入联系人",
                        手机号: "请输入手机号",
                        始发地: "请输入码头名称或城市选择",
                        目的地: "请输入码头名称或城市选择",
                        船舶名称: "请输入关键字选择承运船舶",
                        船主联系人: "请输入联系人",
                        船主手机号: "请输入手机号"
                    }[label]
                }
                value={(form[key] as string | number | undefined) ?? ""}
                onChange={event => setField(key, event.target.value as never)}
            />
        </label>
    );

    /** 保留 Vue 装载日期的展示格式、提交格式及字段绑定。 */
    const loadDateField = (
        <label>
            <span>
                <RequiredMark />
                装载日期
            </span>
            <ZhDatePicker
                aria-label="装载日期"
                type="date"
                format="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                placeholder="请选择装载日期"
                value={form.loadDate}
                onChange={value => setField("loadDate", typeof value === "string" ? value : undefined)}
            />
        </label>
    );

    const numberField = (label: string, key: keyof WaybillManagementFormData) => (
        <label>
            <span>
                {requiredNumberLabels.has(label) ? <RequiredMark /> : null}
                {label}
            </span>
            <Input
                aria-label={label}
                inputMode="decimal"
                placeholder={
                    {
                        "货主 ID": "请输入货主ID",
                        "始发地 ID": "请输入始发地ID",
                        "目的地 ID": "请输入目的地ID",
                        运价: "请输入运价",
                        运货吨数: "请输入运货吨数",
                        装载天数: "请选择宽限天数",
                        装卸天数: "请输入装卸天数",
                        "船舶 ID": "请输入船舶ID",
                        "船主 ID": "请输入船主ID"
                    }[label]
                }
                value={(form[key] as number | undefined) ?? ""}
                onChange={event => setField(key, numberValue(event.target.value) as never)}
            />
        </label>
    );

    return (
        <section aria-label="waybill-management-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {isEdit ? "修改船运单" : "新增船运单"}
                </Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <form
                    onSubmit={event => {
                        event.preventDefault();
                        void submit();
                    }}
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {textField("货物类型", "goodsType")}
                        {numberField("货主 ID", "ownerId")}
                        {textField("联系人", "ownerName")}
                        {textField("手机号", "ownerMobile")}
                        {numberField("始发地 ID", "sourcePortId")}
                        {textField("始发地", "sourcePortName")}
                        {numberField("目的地 ID", "destinationPortId")}
                        {textField("目的地", "destinationPortName")}
                        {numberField("运价", "unitPrice")}
                        {numberField("运货吨数", "ton")}
                        {loadDateField}
                        {numberField("装载天数", "loadDays")}
                        {numberField("装卸天数", "loadUnloadDays")}
                        {numberField("船舶 ID", "shipId")}
                        {textField("船舶名称", "shipName")}
                        {numberField("船主 ID", "captainId")}
                        {textField("船主联系人", "captainName")}
                        {textField("船主手机号", "captainMobile")}
                    </div>
                    <Space className="operation-fixed-btn mt-6">
                        <Button aria-label="提交" htmlType="submit" type="primary">
                            提交
                        </Button>
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </form>
            </Card>
            <ZhDialog
                open={pendingPayload != null}
                title="提示"
                width={400}
                closeOnClickModal={false}
                onCancel={() => setPendingPayload(null)}
                footer={
                    <Space>
                        <Button onClick={() => setPendingPayload(null)}>取消</Button>
                        <Button type="primary" onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Typography.Text>是否保存</Typography.Text>
            </ZhDialog>
        </section>
    );
}

export default WaybillManagementFormPage;
